import { VidMuseEvent } from "@/common/events";
import { logger } from "@/common/logger";
import { useFullscreen } from "@/hooks/useFullscreen";
import { useSpacePlayback } from "@/hooks/useSpacePlayback";
import { useThreadTimelinePlayback } from "@/hooks/useThreadTimelinePlayback";
import { usePinch, useWheel } from "@use-gesture/react";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { AudioPlayer } from "./components/AudioPlayer";
import { PreviewPanel } from "./components/PreviewPanel/index";
import { TimelineControls } from "./components/TimelineControls";
import { TimelineTracks } from "./components/tracks/TimelineTracks";
import { getTimelineDuration } from "./logic/timelineLayout";
import { deriveVisualState } from "./logic/timelinePlayback";
import {
  MAX_ZOOM,
  MIN_ZOOM,
  clampZoom,
  getStoredZoom,
  setStoredZoom,
} from "./logic/zoom";
import { EmptyTrack, TimelineEditorProps, TimelineTime } from "./types";

export * from "./types";

const MIN_TIMELINE_DURATION = 0;
const BASE_PIXELS_PER_SECOND = 100;

export const TimelineEditor = observer(function TimelineEditor({
  className,
  isActive = true,
  tracks,
  onSelectItem,
  visualTrackKind,
  threadId,
}: TimelineEditorProps) {
  // 本地维护当前播放时间，作为单一数据源
  const [currentTime, setCurrentTime] = useState(0);

  const visibleTracks = useMemo(() => {
    if (!visualTrackKind) return tracks;

    const filteredTracks = tracks.filter(
      (track) =>
        track.kind === "music" ||
        track.kind === "audio" ||
        track.kind === "lyrics" ||
        track.kind === visualTrackKind
    );

    if (filteredTracks.length === 0) {
      return [{ id: "empty", kind: "empty", items: [] }] as EmptyTrack[];
    }

    return filteredTracks;
  }, [visualTrackKind, tracks]);

  const totalDuration = useMemo(
    () => Math.max(getTimelineDuration(tracks), MIN_TIMELINE_DURATION),
    [tracks]
  );
  const visualState = useMemo(
    () => deriveVisualState(tracks, currentTime, visualTrackKind),
    [tracks, currentTime, visualTrackKind]
  );
  const [zoom, setZoom] = useState(() => getStoredZoom(threadId));
  const { playbackMode, setPlaybackMode } = useThreadTimelinePlayback(threadId);

  useEffect(() => {
    setStoredZoom(threadId, zoom);
  }, [threadId, zoom]);

  useEffect(() => {
    if (!isActive) {
      setIsPlaying(false);
    }
  }, [isActive]);

  // 曝光检测：当组件进入视口时发送事件
  useEffect(() => {
    logger.event(VidMuseEvent.StudioDisplayed);
  }, []);

  const pixelsPerSecond = useMemo(() => BASE_PIXELS_PER_SECOND * zoom, [zoom]);
  const pixelsPerMs = useMemo(() => pixelsPerSecond / 1000, [pixelsPerSecond]);
  const [isPlaying, setIsPlaying] = useState(false);
  const rafRef = useRef<number | null>(null);
  const playAnchorTimeRef = useRef<TimelineTime>(currentTime);
  const playAnchorTsRef = useRef<number>(performance.now());
  const tracksScrollRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { isFullscreen, toggleFullscreen } = useFullscreen(containerRef);
  const tracksWidth = useMemo(
    () => Math.max((totalDuration + 1000) * pixelsPerMs, 240),
    [pixelsPerMs, totalDuration]
  );
  const gestureTargetRef = useRef<HTMLDivElement | null>(null);

  const stopPlayback = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleUserSeek = (time: TimelineTime) => {
    setCurrentTime(time);
    playAnchorTimeRef.current = time;
    playAnchorTsRef.current = performance.now();
  };

  useEffect(() => {
    if (!isPlaying) return undefined;

    const tick = (timestamp: number) => {
      const elapsed = timestamp - playAnchorTsRef.current;
      const nextTime = Math.min(
        totalDuration,
        playAnchorTimeRef.current + elapsed
      );
      setCurrentTime(nextTime);

      if (nextTime >= totalDuration) {
        stopPlayback();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [isPlaying, totalDuration, stopPlayback]);

  const handlePlayPause = useCallback(() => {
    setIsPlaying((prev) => {
      const willPlay = !prev;
      if (willPlay) {
        if (currentTime === totalDuration) {
          playAnchorTimeRef.current = 0;

          const container = tracksScrollRef.current;
          if (container) {
            requestAnimationFrame(() => {
              container.scrollTo({ left: 0, behavior: "smooth" });
            });
          }
        } else {
          playAnchorTimeRef.current = currentTime;
        }

        playAnchorTsRef.current = performance.now();
      }
      return willPlay;
    });
  }, [currentTime, totalDuration]);

  const handleSpacePlayToggle = useCallback(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    handlePlayPause();
    return true;
  }, [handlePlayPause]);

  useSpacePlayback(handleSpacePlayToggle, {
    priority: 0,
    enabled: isActive,
  });

  usePinch(
    ({ offset: [, zoomOffset], event, origin }) => {
      const container = tracksScrollRef.current;
      if (!container) return;
      event?.preventDefault();
      const factor = zoomOffset || 1;
      const nextZoom = clampZoom(zoom * factor);
      if (nextZoom === zoom) return;

      const rect = container.getBoundingClientRect();
      const offsetX = origin
        ? origin[0] - rect.left + container.scrollLeft
        : container.scrollLeft + container.clientWidth / 2;
      const anchorTime = Math.max(0, offsetX / pixelsPerMs);
      const nextPixelsPerMs = (BASE_PIXELS_PER_SECOND * nextZoom) / 1000;
      const newScrollLeft =
        anchorTime * nextPixelsPerMs -
        (origin ? origin[0] - rect.left : container.clientWidth / 2);

      setZoom(nextZoom);
      requestAnimationFrame(() => {
        container.scrollTo({ left: newScrollLeft, behavior: "auto" });
      });
    },
    { target: gestureTargetRef }
  );

  useWheel(
    ({ event, ctrlKey, delta: [, dy] }) => {
      if (!ctrlKey) return;
      const container = tracksScrollRef.current;
      if (!container) return;
      event.preventDefault();
      const rect = container.getBoundingClientRect();
      const offsetX = event.clientX - rect.left + container.scrollLeft;
      const anchorTime = Math.max(0, offsetX / pixelsPerMs);
      const factor = 1 - dy * 0.001;
      const nextZoom = clampZoom(zoom * factor);
      if (nextZoom === zoom) return;
      const nextPixelsPerMs = (BASE_PIXELS_PER_SECOND * nextZoom) / 1000;
      const newScrollLeft =
        anchorTime * nextPixelsPerMs - (event.clientX - rect.left);
      setZoom(nextZoom);
      requestAnimationFrame(() => {
        container.scrollTo({ left: newScrollLeft, behavior: "auto" });
      });
    },
    { target: gestureTargetRef, eventOptions: { passive: false } }
  );

  const isAutoScrollingRef = useRef(false);
  const autoScrollTimerRef = useRef<number | null>(null);
  useEffect(() => {
    const container = tracksScrollRef.current;
    if (!container) return;

    if (!isPlaying) {
      isAutoScrollingRef.current = false;
      if (autoScrollTimerRef.current) {
        clearTimeout(autoScrollTimerRef.current);
        autoScrollTimerRef.current = null;
      }
      return;
    }

    const markerLeft = currentTime * pixelsPerMs;
    const viewEnd = container.scrollLeft + container.clientWidth;
    const margin = 16;

    if (!isAutoScrollingRef.current && markerLeft > viewEnd - margin) {
      isAutoScrollingRef.current = true;
      container.scrollTo({ left: markerLeft, behavior: "smooth" });
      autoScrollTimerRef.current = window.setTimeout(() => {
        isAutoScrollingRef.current = false;
        autoScrollTimerRef.current = null;
      }, 400);
    }
  }, [currentTime, isPlaying, pixelsPerMs]);

  const registerContainer = useCallback((node: HTMLDivElement | null) => {
    tracksScrollRef.current = node;
    gestureTargetRef.current = node;
  }, []);

  return (
    <div
      ref={containerRef}
      className={twMerge(
        classNames(
          "relative w-full flex flex-col gap-4 pt-5 h-full pb-4 min-w-0 outline-none",
          isFullscreen && "h-full bg-background p-6 overflow-hidden"
        ),
        className
      )}
      tabIndex={-1}
    >
      <PreviewPanel
        activeTime={currentTime}
        visualState={visualState}
        onSelectItem={onSelectItem}
        isPlaying={isPlaying}
        className={isFullscreen ? "flex-1 min-h-0 h-auto" : undefined}
        visualTrackKind={visualTrackKind}
      />

      <AudioPlayer
        tracks={tracks}
        activeTime={currentTime}
        isPlaying={isPlaying}
      />

      <div className="rounded-xl bg-background-card text-text-primary">
        <TimelineControls
          activeTime={currentTime}
          totalDuration={totalDuration}
          isPlaying={isPlaying}
          zoom={zoom}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          playbackMode={playbackMode}
          onPlayPause={handlePlayPause}
          onZoomChange={setZoom}
          onPlaybackModeChange={setPlaybackMode}
          onFullscreen={toggleFullscreen}
          isFullscreen={isFullscreen}
          visualTrackKind={visualTrackKind}
        />

        <div className="p-5 pb-0">
          <TimelineTracks
            tracks={visibleTracks}
            trackWidth={tracksWidth}
            activeTime={currentTime}
            totalDuration={totalDuration}
            pixelsPerMs={pixelsPerMs}
            onSeek={(time) => {
              // 用户点击或拖拽时间轴，暂停播放并 seek
              if (isPlaying) {
                setIsPlaying(false);
              }
              handleUserSeek(time);
            }}
            onSelectItem={onSelectItem}
            registerContainer={registerContainer}
          />
        </div>
      </div>
    </div>
  );
});
